import { Component, Element, Prop, VNode, Watch, h } from '@stencil/core';

import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { getAllElementsWithSlotAttribute, isNullOrUndefined } from 'utils/index';

/**
 * The masthead is a customisable site-wide header that contains main navigation breadcrumbs (see ads-breadcrumb component) and the page title.
 * The masthead is displayed full width at the top of the page. The background can be configured to use colours or images by using utility classes.
 * @tag `<ads-masthead>`
 * @slot breadcrumbs - The breadcrumbs that will be displayed inside the component
 * @slot heading - The heading that will be displayed inside the component
 * @slot - Content rendered as rich text inside the component
 * @example
 * <ads-masthead>
 *   <ads-breadcrumbs theme="dark" slot="breadcrumbs" sr-label="Breadcrumbs">
 *     <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text1">Text1</ads-breadcrumb>
 *     <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text2">Text2</ads-breadcrumb>
 *     <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text3">Text3</ads-breadcrumb>
 *     <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text4" aria-current="true">Text4</ads-breadcrumb>
 *   </ads-breadcrumbs>
 *   <h1 slot="heading"><span>Lorem ipsum dolor sit amet</span></h1>
 * </ads-masthead>
 */
@Component({
  tag: 'ads-masthead',
  styleUrl: 'ads-masthead.scss',
  shadow: true,
})
export class AdsMasthead implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-masthead'));
    this.component = 'ads-masthead';
  }

  @Element() private el: HTMLAdsMastheadElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-masthead'));
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const $breadcrumbsEl = getAllElementsWithSlotAttribute(this.el, 'breadcrumbs')[0];
    const $breadcrumbs = !isNullOrUndefined($breadcrumbsEl)
      ? ((
          <div class="c-masthead__breadcrumbs">
            <slot name="breadcrumbs"></slot>
          </div>
        ) as HTMLElement)
      : null;

    const $headingEl = getAllElementsWithSlotAttribute(this.el, 'heading')[0];
    const $heading = !isNullOrUndefined($headingEl)
      ? ((
          <div class="c-masthead__heading">
            <slot name="heading"></slot>
          </div>
        ) as HTMLElement)
      : null;

    return (
      <div class="c-masthead">
        <div class="c-masthead__top">{$breadcrumbs}</div>
        {$heading}
        <slot />
      </div>
    ) as VNode;
  }
}
